import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Check, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/site/Reveal";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE, AMAZON_STORE } from "@/data/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-40 pb-16" data-testid="contact-hero">
        <p className="text-xs uppercase tracking-[0.35em] text-rosegold mb-7">Say Hello</p>
        <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
          <MaskedLines lines={["Let's Stay"]} delay={0.2} />
          <MaskedLines lines={["In Touch."]} delay={0.34} wordClass="italic text-rosegold" />
        </h1>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-32 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-charcoal/60 leading-relaxed text-lg max-w-sm">
              We'd love to hear from you — whether it's a question, a kind word, or a little styling inspiration.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 space-y-8">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-charcoal/40 mb-3">Instagram</p>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="inline-flex items-center gap-2 font-serif text-3xl hover:text-rosegold transition-colors duration-300">
                  <Instagram size={22} /> {INSTAGRAM_HANDLE}
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-charcoal/40 mb-3">Shop</p>
                <a href={AMAZON_STORE} target="_blank" rel="noopener noreferrer" data-testid="contact-amazon-link" className="inline-flex items-center gap-1 font-serif text-3xl hover:text-rosegold transition-colors duration-300">
                  Amazon Store <ArrowUpRight size={22} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <div className="bg-champagne/50 p-8 sm:p-12">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="thanks"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-16 text-center"
                    data-testid="contact-thankyou"
                  >
                    <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-rosegold/20 text-rosegold">
                      <Check size={26} />
                    </span>
                    <h2 className="font-serif text-4xl tracking-tight">Thank you, {form.name || "lovely"}.</h2>
                    <p className="mt-4 text-charcoal/60 leading-relaxed max-w-sm mx-auto">
                      Your message has been received with love. We'll be in touch soon.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={onSubmit}
                    className="space-y-7"
                    data-testid="contact-form"
                  >
                    <Field label="Name" name="name" value={form.name} onChange={onChange} required />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                    <div>
                      <label className="text-xs uppercase tracking-[0.25em] text-charcoal/50">Message</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        required
                        rows={4}
                        data-testid="contact-message-input"
                        className="mt-3 w-full bg-transparent border-b border-charcoal/25 pb-3 text-lg focus:border-charcoal outline-none transition-colors duration-300 resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="contact-submit-btn"
                      className="group inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-400 hover:bg-rosegold"
                    >
                      Send Message
                      <ArrowUpRight size={15} className="transition-transform duration-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, name, type = "text", value, onChange, required }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.25em] text-charcoal/50">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`contact-${name}-input`}
      className="mt-3 w-full bg-transparent border-b border-charcoal/25 pb-3 text-lg focus:border-charcoal outline-none transition-colors duration-300"
    />
  </div>
);
